<template>
  <div class="rounded-md border border-gray-300 bg-gray-50 z-0">
    <SwitchGroup as="div" class="flex items-center p-4">
      <SwitchLabel
        v-if="label"
        as="span"
        class="flex flex-col gap-1 pr-4 mr-4 border-r border-gray-200 grow"
      >
        <p class="text-sm font-medium text-gray-700">
          {{ label }}
        </p>
        <p class="text-sm text-gray-700">
          {{ description }}
        </p>
      </SwitchLabel>
      <Switch
        :model-value="!!modelValue"
        @update:model-value="emit('switchInput', $event)"
        :class="[
          modelValue
            ? 'bg-gray-600 focus:outline-gray-500 focus:bg-gray-500'
            : 'bg-gray-200 focus:outline-gray-400 focus:bg-gray-400',
          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ',
          {
            'opacity-60 !cursor-not-allowed': disabled,
          },
        ]"
        :disabled="disabled"
      >
        <span
          aria-hidden="true"
          :class="[
            modelValue ? 'translate-x-5' : 'translate-x-0',
            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          ]"
        />
      </Switch>
    </SwitchGroup>
    <!-- Dependent fields -->
    <div
      class="flex flex-col gap-4 bg-white p-4 rounded-b-lg"
      v-if="dependentFields?.length && modelValue"
    >
      <DynamicInputComponent
        v-for="field in dependentFields"
        :key="field.key"
        :modelValue="form[field.key]"
        @update:modelValue="debounceInputHandlers[field.key]"
        :field="field"
        :form="form"
        :limitWidth="false"
      />
    </div>
  </div>
</template>

<script setup>
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";
import DynamicInputComponent from "@/Tailwind/Components/Settings/DynamicInputComponent.vue";
import { useDebounceFn } from "@vueuse/core";

const props = defineProps({
  modelValue: {
    type: [Boolean, String, Number],
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
  },
  description: {
    type: String,
  },
  dependentFields: {
    type: Array,
    default: () => [],
  },
  form: {
    type: Object,
    required: true,
  },
  debounce: Boolean,
});

const emit = defineEmits(["update:modelValue", "switchInput"]);

const debounceInputHandlers = props.dependentFields.reduce((acc, field) => {
  acc[field.key] = useDebounceFn(
    (field, e) => {
      emit("update:modelValue", field, e);
    },
    props.debounce ? 750 : 0
  );

  return acc;
}, {});
</script>
